
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase, type News } from '../../../lib/supabase';
import VideoPlayer from '../../../components/VideoPlayer';
import TabBar from '../../home/components/TabBar';

const NewsDetailPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [news, setNews] = useState<News | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      fetchNews();
    }
  }, [id]);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('news')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error fetching news:', error);
        setNews(null);
        return;
      }

      setNews(data);
    } catch (error) {
      console.error('Error:', error);
      setNews(null);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { 
      day: 'numeric', 
      month: 'long',
      year: 'numeric'
    });
  };

  // Funciones para compartir en redes sociales
  const getShareUrl = () => {
    return window.location.href;
  };

  const getShareText = () => {
    return `${news?.title || 'Noticia'} - VIU L'ESPORT`;
  };

  const shareOnWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(`${getShareText()} ${getShareUrl()}`)}`;
    window.open(url, '_blank');
  };

  const shareOnInstagram = async () => {
    // Instagram no tiene compartidor web directo, así que copiamos el enlace
    // El usuario puede pegarlo en su publicación de Instagram
    try {
      const shareText = `${getShareText()} ${getShareUrl()}`;
      await navigator.clipboard.writeText(shareText);
      alert('Enlace copiado. Pega el enlace en tu publicación de Instagram');
    } catch (err) {
      // Fallback para navegadores antiguos
      const textArea = document.createElement('textarea');
      textArea.value = `${getShareText()} ${getShareUrl()}`;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Enlace copiado. Pega el enlace en tu publicación de Instagram');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black pb-20">
        <div className="sticky top-0 z-10 bg-black/80 backdrop-blur-md border-b border-white/10">
          <div className="px-4 py-4 flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            >
              <i className="ri-arrow-left-line text-white text-xl"></i>
            </button>
            <h1 className="text-lg font-bold text-white">Noticia</h1>
          </div>
        </div>
        
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-3 border-white/20 border-t-pink-500 rounded-full animate-spin"></div>
          <p className="mt-4 text-white/60">Cargando noticia...</p>
        </div>
        
        <TabBar />
      </div>
    );
  }

  if (!news) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black pb-20">
        <div className="sticky top-0 z-10 bg-black/80 backdrop-blur-md border-b border-white/10">
          <div className="px-4 py-4 flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            >
              <i className="ri-arrow-left-line text-white text-xl"></i>
            </button>
            <h1 className="text-lg font-bold text-white">Noticia</h1>
          </div>
        </div>
        
        <div className="text-center py-12 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 mx-4">
          <i className="ri-newspaper-line text-5xl text-white/30 mb-3"></i>
          <p className="text-white/60">Noticia no encontrada</p>
        </div>
        
        <TabBar />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black pb-20">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-black/80 backdrop-blur-md border-b border-white/10">
        <div className="px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <i className="ri-arrow-left-line text-white text-xl"></i>
          </button>
          <h1 className="text-lg font-bold text-white">Noticia</h1>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-6">
        <div className="max-w-2xl mx-auto">
          {news.image && (
            <div className="mb-6 rounded-2xl overflow-hidden">
              <img
                src={news.image}
                alt={news.title}
                className="w-full h-64 object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            </div>
          )}
          {news.video_url && (
            <div className="mb-6">
              <VideoPlayer url={news.video_url} />
            </div>
          )}

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
            <div className="flex items-center gap-2 mb-4">
              <h2 className="text-2xl font-bold text-white flex-1">
                {news.title}
              </h2>
            </div>

            <div className="flex items-center gap-4 mb-6 text-sm text-white/60">
              <div className="flex items-center gap-1">
                <i className="ri-user-line"></i>
                <span>{news.author || 'Admin'}</span>
              </div>
              <div className="flex items-center gap-1">
                <i className="ri-calendar-line"></i>
                <span>{formatDate(news.created_at)}</span>
              </div>
            </div>

            <div className="prose prose-invert max-w-none">
              <p className="text-white/80 leading-relaxed whitespace-pre-wrap">
                {news.text}
              </p>
            </div>
          </div>

          {/* Botones de compartir en redes sociales */}
          <div className="mt-6 bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
            <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <i className="ri-share-line"></i>
              Compartir noticia
            </h3>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={shareOnWhatsApp}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm font-medium"
                title="Compartir en WhatsApp"
              >
                <i className="ri-whatsapp-fill"></i>
                <span className="hidden sm:inline">WhatsApp</span>
              </button>
              
              <button
                onClick={shareOnInstagram}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg transition-colors text-sm font-medium"
                title="Compartir en Instagram"
              >
                <i className="ri-instagram-fill"></i>
                <span className="hidden sm:inline">Instagram</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <TabBar />
    </div>
  );
};

export default NewsDetailPage;
